import { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../api';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { TimePicker24h } from './TimePicker24h';

const METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'twint', label: 'Twint' },
  { value: 'virement', label: 'Virement' },
];

const getLastMethod = () => { try { return localStorage.getItem('bachata_last_method') || 'cash'; } catch { return 'cash'; } };
const saveLastMethod = m => { try { localStorage.setItem('bachata_last_method', m); } catch {} };

export default function PaymentModal({ studentId, studentName, packages = [], groupCourses = [], onClose, onSaved }) {
  const today = format(new Date(), 'yyyy-MM-dd');
  const hasPackages = packages.length > 0;
  const hasGroupCourses = groupCourses.length > 0;
  const defaultTarget = hasPackages ? 'package' : hasGroupCourses ? 'group' : 'libre';

  const [paymentTarget, setPaymentTarget] = useState(defaultTarget);
  const [selectedPkgId, setSelectedPkgId] = useState(packages[0]?.id || '');
  const [selectedCourseId, setSelectedCourseId] = useState(groupCourses[0]?.course_id || '');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(today);
  const [time, setTime] = useState(format(new Date(), 'HH:mm'));
  const [method, setMethod] = useState(getLastMethod);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const suggestedAmount = () => {
    if (paymentTarget === 'package') {
      const pkg = packages.find(p => p.id === selectedPkgId);
      if (pkg) return Math.max(0, pkg.total_price - (pkg.paid_amount || 0));
    } else if (paymentTarget === 'group') {
      const gc = groupCourses.find(c => c.course_id === selectedCourseId);
      if (gc) return Math.max(0, gc.balance);
    }
    return null;
  };

  // Auto-fill amount when target/selection changes
  useEffect(() => {
    const s = suggestedAmount();
    if (s > 0) setAmount(String(s));
  }, [paymentTarget, selectedPkgId, selectedCourseId]);

  const handleMethodChange = m => { setMethod(m); saveLastMethod(m); };

  const handleSave = async () => {
    if (!amount || parseFloat(amount) <= 0) { toast.error('Montant invalide'); return; }
    if (paymentTarget === 'package' && !selectedPkgId) { toast.error('Sélectionnez un forfait'); return; }
    if (paymentTarget === 'group' && !selectedCourseId) { toast.error('Sélectionnez un cours'); return; }
    setSaving(true);
    try {
      const datetimeStr = `${date}T${time || '00:00'}`;
      await api.post('/payments', {
        student_id: studentId,
        amount: parseFloat(amount),
        date: datetimeStr,
        method,
        notes,
        package_id: paymentTarget === 'package' ? selectedPkgId : null,
        group_course_id: paymentTarget === 'group' ? selectedCourseId : null,
      });
      saveLastMethod(method);
      toast.success(`✓ ${amount} CHF encaissés${studentName ? ` – ${studentName}` : ''}`);
      onSaved();
    } catch (e) {
      toast.error(e.message || 'Erreur');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-0 md:p-4"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full md:max-w-sm rounded-t-3xl md:rounded-2xl shadow-2xl flex flex-col"
        style={{ maxHeight: '92dvh' }} data-testid="payment-modal">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <div>
            <div className="font-bold text-slate-900">Ajouter un paiement</div>
            {studentName && <div className="text-xs text-slate-400">{studentName}</div>}
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200">
            <X size={16} />
          </button>
        </div>

        {/* ── Scrollable form ── */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">

          {/* Type selector */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Type</label>
            <div className="flex gap-2">
              {[
                { value: 'package', label: 'Forfait', show: hasPackages },
                { value: 'group', label: 'Collectifs', show: hasGroupCourses },
                { value: 'libre', label: 'Libre', show: true },
              ].filter(t => t.show).map(t => (
                <button key={t.value} type="button" onClick={() => setPaymentTarget(t.value)}
                  className={cn('flex-1 py-2.5 rounded-xl text-sm font-bold transition-colors border',
                    paymentTarget === t.value ? 'bg-[#26A69A] text-white border-transparent' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50')}
                  data-testid={`payment-type-${t.value}`}>{t.label}</button>
              ))}
            </div>
          </div>

          {/* Package selector */}
          {paymentTarget === 'package' && hasPackages && (
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Forfait</label>
              <div className="space-y-2">
                {packages.map(pkg => {
                  const remaining = Math.max(0, pkg.total_price - (pkg.paid_amount || 0));
                  return (
                    <button key={pkg.id} type="button" onClick={() => setSelectedPkgId(pkg.id)}
                      className={cn('w-full flex justify-between items-center p-3 rounded-xl border text-left transition-all',
                        selectedPkgId === pkg.id ? 'border-[#26A69A] bg-[#26A69A]/5' : 'border-slate-200 hover:border-slate-300')}
                      data-testid={`select-pkg-${pkg.id}`}>
                      <div>
                        <div className="text-sm font-bold text-slate-800">
                          {pkg.type === 'group' ? 'Collectif' : 'Privé'}
                          {pkg.type === 'group' ? ` · ${pkg.total_classes} cours` : ` · ${pkg.total_hours}h`}
                        </div>
                        <div className="text-xs text-slate-400">{pkg.total_price} CHF total</div>
                      </div>
                      <span className={cn('text-sm font-bold', remaining > 0 ? 'text-red-500' : 'text-[#26A69A]')}>
                        {remaining > 0 ? `-${remaining} CHF` : 'Soldé'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Group course selector */}
          {paymentTarget === 'group' && hasGroupCourses && (
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Cours collectif</label>
              <div className="space-y-2">
                {groupCourses.map(gc => (
                  <button key={gc.course_id} type="button" onClick={() => setSelectedCourseId(gc.course_id)}
                    className={cn('w-full flex justify-between items-center p-3 rounded-xl border text-left transition-all',
                      selectedCourseId === gc.course_id ? 'border-[#0288D1] bg-[#0288D1]/5' : 'border-slate-200 hover:border-slate-300')}
                    data-testid={`select-course-${gc.course_id}`}>
                    <div>
                      <div className="text-sm font-bold text-slate-800">{gc.course_name}</div>
                      <div className="text-xs text-slate-400">{gc.present_count} séances · {gc.price_per_class} CHF/cours</div>
                    </div>
                    <span className={cn('text-sm font-bold', gc.balance > 0 ? 'text-red-500' : gc.balance < 0 ? 'text-purple-600' : 'text-[#26A69A]')}>
                      {gc.balance > 0 ? `-${gc.balance} CHF` : gc.balance < 0 ? `+${Math.abs(gc.balance)} crédit` : 'Soldé'}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {paymentTarget === 'libre' && (
            <p className="text-xs text-slate-400 bg-slate-50 rounded-xl px-3 py-2.5">
              Paiement libre — non rattaché à un forfait ou cours.
            </p>
          )}

          {/* Amount — pre-filled, always visible */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Montant (CHF)</label>
              {suggestedAmount() > 0 && parseFloat(amount) !== suggestedAmount() && (
                <button type="button" onClick={() => setAmount(String(suggestedAmount()))}
                  className="text-xs text-[#0288D1] font-semibold hover:underline"
                  data-testid="fill-remaining-btn">
                  Solde dû : {suggestedAmount()} CHF
                </button>
              )}
            </div>
            <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
              placeholder="0.00" min="0" step="0.01"
              className="w-full h-14 px-4 bg-slate-50 border border-slate-200 rounded-xl text-2xl font-bold focus:outline-none focus:ring-2 focus:ring-[#26A69A]"
              data-testid="payment-amount" autoFocus />
          </div>

          {/* Method — always visible */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Méthode</label>
            <div className="flex gap-2">
              {METHODS.map(m => (
                <button key={m.value} type="button" onClick={() => handleMethodChange(m.value)}
                  className={cn('flex-1 py-2.5 rounded-xl text-sm font-bold transition-colors',
                    method === m.value ? 'bg-[#26A69A] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}
                  data-testid={`method-${m.value}`}>{m.label}</button>
              ))}
            </div>
          </div>

          {/* "Plus d'options" toggle — date, time, notes hidden by default */}
          <button type="button" onClick={() => setShowMore(v => !v)}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-[#0288D1] transition-colors w-full"
            data-testid="toggle-more-options">
            {showMore ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            {showMore ? 'Moins d\'options' : 'Plus d\'options (date, heure, notes)'}
          </button>

          {showMore && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Date</label>
                  <input type="date" value={date} onChange={e => setDate(e.target.value)}
                    className="w-full h-10 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#26A69A]"
                    data-testid="payment-date" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Heure</label>
                  <TimePicker24h value={time} onChange={setTime} data-testid="payment-time" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Notes (optionnel)</label>
                <input value={notes} onChange={e => setNotes(e.target.value)}
                  placeholder="Paiement mensuel..."
                  className="w-full h-10 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none"
                  data-testid="payment-notes" />
              </div>
            </>
          )}
        </div>

        {/* ── Sticky footer ── */}
        <div className="shrink-0 px-5 pt-3 pb-5 border-t border-slate-100 bg-white">
          <button onClick={handleSave} disabled={saving || !amount || parseFloat(amount) <= 0}
            className="w-full h-14 rounded-2xl bg-[#26A69A] text-white font-bold text-base hover:bg-[#219e92] disabled:opacity-50 active:scale-[0.98] transition-all"
            data-testid="confirm-payment-btn">
            {saving ? 'Enregistrement…' : `Enregistrer${amount && parseFloat(amount) > 0 ? ` · ${amount} CHF` : ''}`}
          </button>
          <button onClick={onClose} className="w-full h-10 mt-2 rounded-xl text-slate-500 text-sm font-medium hover:bg-slate-50 transition-colors">
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
}
